'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebook, FaShoppingBag } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import {
  beginReviewerOAuth,
  type ReviewerAuthProvider,
} from '../utils/reviewerApi';

// ── Props ─────────────────────────────────────────────────────

interface ReviewerLoginPromptProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  title?: string;
  subtitle?: string;
  description?: string;
  showSkip?: boolean;
  onSkip?: () => void;
}

// ── Component ─────────────────────────────────────────────────

const ReviewerLoginPrompt: React.FC<ReviewerLoginPromptProps> = ({
  isOpen,
  onClose,
  onLoginSuccess: _onLoginSuccess,
  title = 'Sign in to continue',
  subtitle,
  description,
  showSkip = false,
  onSkip,
}) => {
  const displaySubtitle = subtitle || description || 'We only use your name and email. No spam, ever.';
  const [visible, setVisible] = useState(false);
  const [loadingProvider, setLoadingProvider] =
    useState<ReviewerAuthProvider | null>(null);

  // Animate in/out
  useEffect(() => {
    if (isOpen) {
      // Small delay so the CSS transition triggers
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  const handleOAuth = useCallback(
    async (provider: ReviewerAuthProvider) => {
      try {
        setLoadingProvider(provider);
        await beginReviewerOAuth(provider);
        // The browser will redirect; no need to close
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
        message.error(errorMessage);
        setLoadingProvider(null);
      }
    },
    [],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Card */}
      <div
        className={`relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-8 transform transition-all duration-300 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <IoCloseOutline size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-sm text-gray-500">{displaySubtitle}</p>
        </div>

        {/* OAuth buttons */}
        <div className="flex flex-col gap-3">
          {/* Google */}
          <button
            onClick={() => handleOAuth('google')}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center w-full gap-3 px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FcGoogle size={20} />
            {loadingProvider === 'google' ? 'Redirecting...' : 'Continue with Google'}
          </button>

          {/* Facebook */}
          <button
            onClick={() => handleOAuth('facebook')}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center w-full gap-3 px-4 py-3 rounded-lg font-medium text-white hover:opacity-90 active:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#1877F2' }}
          >
            <FaFacebook size={20} />
            {loadingProvider === 'facebook'
              ? 'Redirecting...'
              : 'Continue with Facebook'}
          </button>

          {/* Shopify */}
          <button
            onClick={() => handleOAuth('shopify')}
            disabled={loadingProvider !== null}
            className="flex items-center justify-center w-full gap-3 px-4 py-3 rounded-lg font-medium text-white hover:opacity-90 active:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#96BF48' }}
          >
            <FaShoppingBag size={18} />
            {loadingProvider === 'shopify'
              ? 'Redirecting...'
              : 'Continue with Shopify'}
          </button>
        </div>

        {/* Privacy note */}
        <p className="text-xs text-gray-400 text-center mt-5 leading-relaxed">
          By signing in you agree to our{' '}
          <a href="/terms" className="underline hover:text-gray-600">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy" className="underline hover:text-gray-600">
            Privacy Policy
          </a>
          .
        </p>

        {/* Skip / Cancel */}
        <div className="text-center mt-4 flex flex-col gap-2">
          {showSkip && onSkip && (
            <button
              onClick={onSkip}
              className="text-sm font-medium text-sky-600 hover:text-sky-700 transition-colors"
            >
              Continue as guest
            </button>
          )}
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewerLoginPrompt;
